import React, { useEffect, useState, useCallback } from 'react';
import { Menu, MenuHandler, MenuList, MenuItem } from "@material-tailwind/react";
import Pagination from '../../../components/Pagination/Pagination';
import CourseCard from '../../../components/Cours/CourseCard';
import { ListFilterIcon } from 'lucide-react';
import FiltersPanel from './FiltersPanel';
import { useNavigate } from 'react-router-dom';
import coursesService from '../../../services/coursesService';
import categoriesService from '../../../services/categoriesService';

const sortItems = ['Plus Populaire', 'Plus Récent', 'Mieux Noté', 'Prix: Croissant', 'Prix: Décroissant'];

const CoursesSection = () => {
   const [showFilters, setShowFilters] = useState(false);
   const [sortBy, setSortBy] = useState("Plus Populaire");
   const [currentPage, setCurrentPage] = useState(1);
   const [courses, setCourses] = useState([]);
   const [categories, setCategories] = useState([]);
   const [filteredCourses, setFilteredCourses] = useState([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
   const [filters, setFilters] = useState({
      category: [],
      rating: null,
      instructors: [],
      price: 'All',
      level: [],
      language: [],
      duration: []
   });

   const navigate = useNavigate();
   const coursesPerPage = 9;

   // Charger les cours
   const fetchCourses = useCallback(async () => {
      setLoading(true);
      setError(null);
      try {
         const data = await coursesService.getAllCourses();
         const list = Array.isArray(data) ? data : (data?.data ?? []);
         setCourses(list);
         setFilteredCourses(list);
      } catch (err) {
         setError('Erreur de connexion au serveur');
         console.error('Erreur:', err);
      } finally {
         setLoading(false);
      }
   }, []);

   // Charger les catégories
   const fetchCategories = useCallback(async () => {
      try {
         const data = await categoriesService.getAllCategories();
         const list = Array.isArray(data) ? data : (data?.data ?? []);
         setCategories(list);
      } catch (err) {
         console.error('Erreur catégories:', err);
      }
   }, []);

   // Appliquer les filtres
   const applyFilters = useCallback(() => {
      let result = [...courses];

      // Filtre par catégorie
      if (filters.category.length > 0) {
         result = result.filter(course =>
            filters.category.includes(course.category_id?.toString())
         );
      }

      // Filtre par rating
      if (filters.rating) {
         result = result.filter(course =>
            course.rating >= parseFloat(filters.rating)
         );
      }

      // Filtre par instructeurs
      if (filters.instructors.length > 0) {
         result = result.filter(course =>
            filters.instructors.includes(course.primary_instructor_id?.toString())
         );
      }

      // Filtre par prix
      if (filters.price === 'Free') {
         result = result.filter(course => course.price === 0);
      } else if (filters.price === 'Paid') {
         result = result.filter(course => course.price > 0);
      }

      // Filtre par niveau
      if (filters.level.length > 0) {
         result = result.filter(course =>
            filters.level.includes(course.difficulty_level)
         );
      }

      // Filtre par langue
      if (filters.language.length > 0) {
         result = result.filter(course =>
            filters.language.includes(course.language)
         );
      }

      // Filtre par durée
      if (filters.duration.length > 0) {
         result = result.filter(course => {
            const duration = course.estimated_duration_hours;
            return filters.duration.some(filter => {
               if (filter === 'Less than 3 hours') return duration < 3;
               if (filter === '4 - 7 hours') return duration >= 4 && duration <= 7;
               if (filter === '8 - 18 hours') return duration >= 8 && duration <= 18;
               if (filter === '20 + Hours') return duration >= 20;
               return false;
            });
         });
      }

      // Tri des résultats
      result = sortCourses(result, sortBy);

      setFilteredCourses(result);
      setCurrentPage(1); // Reset à la première page après filtrage
   }, [courses, filters, sortBy]);

   // Trier les cours
   const sortCourses = (coursesList, sortOption) => {
      const sorted = [...coursesList];

      switch (sortOption) {
         case 'Newest':
            return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
         case 'Highest Rated':
            return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
         case 'Price: Low to High':
            return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
         case 'Price: High to Low':
            return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
         case 'Most Popular':
         default:
            return sorted.sort((a, b) => (b.total_enrollements || 0) - (a.total_enrollements || 0));
      }
   };

   // Gestion des changements de filtres
   const handleFilterChange = (filterType, value) => {
      setFilters(prev => {
         if (filterType === 'rating' || filterType === 'price') {
            return { ...prev, [filterType]: value };
         }

         return {
            ...prev,
            [filterType]: prev[filterType].includes(value)
               ? prev[filterType].filter(item => item !== value)
               : [...prev[filterType], value]
         };
      });
   };

   // Pagination
   const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);
   const indexOfLastCourse = currentPage * coursesPerPage;
   const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
   const currentCourses = filteredCourses.slice(indexOfFirstCourse, indexOfLastCourse);

   const handleCourseClick = (course) => {
      navigate(`/courses/${course.slug || course.id}`);
   };

   useEffect(() => {
      fetchCourses();
      fetchCategories();
   }, [fetchCourses, fetchCategories]);

   useEffect(() => {
      applyFilters();
   }, [applyFilters]);

   if (loading) {
      return (
         <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
         </div>
      );
   }

   if (error) {
      return (
         <section className="pb-16 dark:border-b dark:border-light-2">
            <div className="container mx-auto px-4">
               <div className="text-center py-12 text-red-500">{error}</div>
            </div>
         </section>
      );
   }

   return (
      <section className="pb-16 dark:border-b dark:border-light-2">
         <div className="container mx-auto px-4">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-center pb-8 border-b border-gray-200">
               <div className="text-sm mb-4 md:mb-0 text-gray-400">
                  Showing <span className="font-medium text-dark-1">{filteredCourses.length}</span> total results
               </div>

               <div className="flex items-center space-x-2">
                  <div className="flex items-center">
                     <span className="text-xs font-medium text-dark-1 mr-4">Trie:</span>
                     <Menu>
                        <MenuHandler>
                           <button className="flex items-center bg-light-3 px-[20px] py-3 border-none rounded-lg text-dark-1 text-sm font-medium">
                              {sortBy}
                              <svg
                                 xmlns="http://www.w3.org/2000/svg"
                                 className="h-4 w-4 ml-2"
                                 fill="none"
                                 viewBox="0 0 24 24"
                                 stroke="currentColor"
                              >
                                 <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                 />
                              </svg>
                           </button>
                        </MenuHandler>
                        <MenuList className="bg-white px-5 py-[10px] w-[146px]">
                           {sortItems.map((sortItem) => (
                              <MenuItem
                                 key={sortItem}
                                 onClick={() => setSortBy(sortItem)}
                                 className='hover:text-blue-600 px-[15px] pt-[8px] w-fit text-sm items-center text-gray-500'
                              >
                                 {sortItem}
                              </MenuItem>
                           ))}
                        </MenuList>
                     </Menu>
                  </div>

                  <button
                     onClick={() => setShowFilters(!showFilters)}
                     className="flex items-center px-6 py-3 bg-[#E5F0FD] text-purple-1 rounded-lg hover:bg-purple1text-purple-1 hover:text-white transition-colors duration-200"
                  >
                     <ListFilterIcon className="w-4 h-4 mr-2" />
                     Filtre
                  </button>
               </div>
            </div>

            {/* Filtres */}
            <FiltersPanel
               showFilters={showFilters}
               onFilterChange={handleFilterChange}
               selectedFilters={filters}
               categories={categories}
            />

            {/* Liste des cours */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 my-8">
               {currentCourses.map((course, index) => (
                  <div key={course.id} className="flex justify-center">
                     <CourseCard
                        course={{
                           id: course.id,
                           title: course.title,
                           image: course.image_url,
                           rating: course.rating,
                           total_reviews: course.total_reviews,
                           total_lessons: course.total_lessons,
                           estimated_duration_hours: course.estimated_duration_hours,
                           difficulty_level: course.difficulty_level,
                           instructor_name: course.instructor_name,
                           instructor_avatar: course.instructor_avatar,
                           price: course.price,
                           discounted_price: course.discounted_price,
                           tags: course.featured && course.id === 2 ? ["POPULAR", "Featured"] : []
                        }}
                        animationDelay={index + 1}
                        onClick={() => handleCourseClick(course)}
                     />
                  </div>
               ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
               <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
               />
            )}
         </div>
      </section>
   );
};

export default CoursesSection;